// ... existing code ...
// ========================================
// API Service Layer - Golang Gin Backend
// Base URL: relative path (same origin via gin.Static)
// ========================================

const API_BASE = '/api/v1';

// ---- Generic API Response Types ----

export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

export interface PaginatedData<T> {
  list: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ---- Green Certificate Transaction Types ----

export interface GreenCertListing {
  id: number;
  userId?: number;
  company: string;
  region: string;
  direction: 'buy' | 'sell';
  year: string;
  halfYear: string[];
  month: string[];
  projectType: string[];
  channel: string[];
  provinceRequirement: string[];
  subsidy: string[];
  quantity: number;
  price: string;
  tradeMethod: string[];
  exchange: string[];
  offerBy: string[];
  tradePrinciple: string[];
  expiryDate: string;
  description: string;
  otherInfo: string;
  publishDate: string;
  contactPerson: string;
  contactPhone: string;
  status: 'active' | 'offline' | 'expired';
  views?: number;
  inquiries?: number;
}

export type SupplyItem = GreenCertListing;
export type DemandItem = GreenCertListing;

export interface GreenCertCreateParams {
  direction: 'buy' | 'sell';
  year: string;
  halfYear: string[];
  month?: string[];
  projectType: string[];
  channel: string[];
  provinceRequirement: string[];
  subsidy: string[];
  quantity: number;
  price: string;
  tradeMethod: string[];
  exchange: string[];
  offerBy?: string[];
  tradePrinciple?: string[];
  expiryDate: string;
  otherInfo: string;
}

export type SupplyCreateParams = GreenCertCreateParams;
export type DemandCreateParams = GreenCertCreateParams;

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterOptions {
  years: string[];
  projectTypes: FilterOption[];
  channels: FilterOption[];
  provinceRequirements: FilterOption[];
  subsidies: FilterOption[];
  tradeMethods: FilterOption[];
  tradePrinciples: FilterOption[];
  exchanges: FilterOption[];
  provinces: FilterOption[];
}

export interface SupplyListParams {
  direction?: string;
  year?: string;
  halfYear?: string;
  month?: string;
  projectType?: string;
  channel?: string;
  provinceRequirement?: string;
  subsidy?: string;
  tradeMethod?: string;
  exchange?: string;
  today?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
}

export interface DemandListParams {
  direction?: string;
  year?: string;
  halfYear?: string;
  month?: string;
  projectType?: string;
  channel?: string;
  provinceRequirement?: string;
  subsidy?: string;
  tradeMethod?: string;
  exchange?: string;
  today?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
}

export interface NewsArticle {
  id: number;
  title: string;
  category: string;
  author: string;
  summary: string;
  content: string;
  tags: string[];
  publishDate: string;
  readCount: number;
  imageSeq: string;
  status?: 'published' | 'draft' | 'offline';
}

export interface EnergySystemData {
  features: {
    id: number;
    icon: string;
    title: string;
    description: string;
  }[];
  advantages: {
    id: number;
    value: string;
    label: string;
    description: string;
  }[];
  caseStudies: {
    id: number;
    company: string;
    industry: string;
    before: string;
    after: string;
    savings: string;
    imageSeq: string;
  }[];
  faq: {
    q: string;
    a: string;
  }[];
  modules: {
    icon: string;
    name: string;
  }[];
}

export interface HomeData {
  marketData: {
    windCertOfficialPrice: string;
    solarCertOfficialPrice: string;
    hydroCertOfficialPrice: string;
    updateDate: string;
    trends: { title: string; date: string }[];
  };
  industryArticles: {
    id: number;
    title: string;
    category: string;
    summary: string;
    date: string;
    imageSeq: string;
  }[];
  stats: {
    totalSupply: number;
    totalDemand: number;
    todayNew: number;
  };
}

export interface ProfileUser {
  id: string;
  accountType: 'personal' | 'company';
  role?: 'supplier' | 'buyer';
  companyName?: string;
  creditCode?: string;
  contactName: string;
  phone: string;
  region?: string;
  email: string;
  avatar: string;
}

export interface PersonalSupply {
  id: number;
  direction: 'sell';
  year: string;
  halfYear: string[];
  month: string[];
  projectType: string[];
  channel: string[];
  provinceRequirement: string[];
  subsidy: string[];
  quantity: number;
  price: string;
  tradeMethod: string[];
  exchange: string[];
  offerBy: string[];
  tradePrinciple: string[];
  expiryDate: string;
  otherInfo: string;
  status: 'active' | 'offline';
  publishDate: string;
  views: number;
}

export interface PersonalDemand {
  id: number;
  direction: 'buy';
  year: string;
  halfYear: string[];
  month: string[];
  projectType: string[];
  channel: string[];
  provinceRequirement: string[];
  subsidy: string[];
  quantity: number;
  price: string;
  tradeMethod: string[];
  exchange: string[];
  offerBy: string[];
  tradePrinciple: string[];
  expiryDate: string;
  otherInfo: string;
  status: 'active' | 'offline';
  publishDate: string;
  inquiries: number;
}

export interface Message {
  id: number;
  type: 'inquiry' | 'system';
  title: string;
  content: string;
  from: string;
  read: boolean;
  time: string;
}

// ---- Chat / Internal Message Types ----

export interface ChatMessage {
  id: number;
  listingId: number;
  listingType: 'supply' | 'demand' | '';
  listingTitle: string;
  senderId: number;
  senderName: string;
  receiverId: number;
  receiverName: string;
  content: string;
  createdAt: string;
  read: boolean;
}

export interface ChatConversation {
  peerId: number;
  listingId: number;
  listingType: 'supply' | 'demand' | '';
  listingTitle: string;
  counterparty: string;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
}

export interface SendChatParams {
  listingId?: number;
  listingType?: 'supply' | 'demand';
  listingTitle?: string;
  receiverId: number;
  receiverName?: string;
  content: string;
}

export const messageApi = {
  conversations: () => request<ChatConversation[]>('/messages/conversations'),

  messages: (peerId: number) =>
    request<ChatMessage[]>(`/messages/conversations/peer/${peerId}`),

  send: (peerId: number, body: SendChatParams) =>
    request<ChatMessage>(`/messages/conversations/${body.listingType ?? 'supply'}/${peerId}`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  markRead: (peerId: number) =>
    request<void>(`/messages/conversations/peer/${peerId}/read`, { method: 'PUT' }),
};

export interface Favorite {
  id: number;
  type: 'supply' | 'demand';
  itemId: number;
  company: string;
  region: string;
  projectType: string[];
  quantity: number;
  year: string;
  price: string;
  savedDate: string;
}

export interface ContactRecord {
  id: number;
  company: string;
  contactPerson: string;
  phone: string;
  inquiryTarget: string;
  time: string;
  note: string;
}

export interface UpdateProfileParams {
  companyName?: string;
  contactName?: string;
  email?: string;
  region?: string;
  avatar?: string;
}

export interface AddFavoriteParams {
  type: 'supply' | 'demand';
  itemId: number;
}

// ---- Base fetch wrapper ----

class ApiError extends Error {
  code: number;
  constructor(code: number, message: string) {
    super(message);
    this.code = code;
    this.name = 'ApiError';
  }
}

async function request<T>(
  endpoint: string,
  options: { method?: string; body?: string; headers?: Record<string, string> } = {},
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  // Attach auth token if available (admin paths use admin_token)
  const userToken = localStorage.getItem('greencert_current_user')
    ? JSON.parse(localStorage.getItem('greencert_current_user') || '').token
    : null;
  const adminToken = localStorage.getItem('admin_token');
  const isAdminPath = endpoint.startsWith('/admin/');
  const bearer = isAdminPath && adminToken ? adminToken : userToken;
  if (bearer) {
    headers['Authorization'] = `Bearer ${bearer}`;
  }

  const res = await fetch(url, { ...options, headers });

  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, errorBody.message || '请求失败');
  }

  const json: ApiResponse<T> = await res.json();

  if (json.code !== 0) {
    throw new ApiError(json.code, json.message || '业务错误');
  }

  return json.data;
}

// ---- Auth API ----

export interface LoginParams {
  phone: string;
  password: string;
  captchaId: string;
  captchaCode: string;
}

export interface RegisterParams {
  accountType: 'personal' | 'company';
  contactName: string;
  phone: string;
  email: string;
  region?: string;
  companyName?: string;
  creditCode?: string;
  password: string;
}

export interface AuthResult {
  user: ProfileUser;
  token: string;
}

export const authApi = {
  login: (params: LoginParams) =>
    request<AuthResult>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  register: (params: RegisterParams) =>
    request<AuthResult>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  me: () => request<ProfileUser>('/auth/me'),

  captcha: () => request<{ captchaId: string; imageBase64: string }>('/auth/captcha'),
  forgotPassword: (email: string) =>
    request<Record<string, never>>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  resetPassword: (token: string, newPassword: string) =>
    request<Record<string, never>>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    }),
};

// ---- Home API ----

export interface HomeApiResponse {
  marketData: HomeData['marketData'];
  industryArticles: HomeData['industryArticles'];
  stats: HomeData['stats'];
  latestSupply: SupplyItem[];
  latestDemand: DemandItem[];
}

export const homeApi = {
  get: () => request<HomeApiResponse>('/home'),
};

// ---- Supply API ----

export const supplyApi = {
  list: (params: SupplyListParams) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.append(key, String(value));
      }
    });
    return request<PaginatedData<SupplyItem>>(
      `/supplies?${searchParams.toString()}`,
    );
  },

  detail: (id: number) => request<SupplyItem>(`/supplies/${id}`),

  create: (data: GreenCertCreateParams) =>
    request<SupplyItem>('/supplies', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  options: () => request<FilterOptions>('/supplies/options'),
};

// ---- Demand API ----

export const demandApi = {
  list: (params: DemandListParams) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.append(key, String(value));
      }
    });
    return request<PaginatedData<DemandItem>>(
      `/demands?${searchParams.toString()}`,
    );
  },

  detail: (id: number) => request<DemandItem>(`/demands/${id}`),

  create: (data: GreenCertCreateParams) =>
    request<DemandItem>('/demands', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  options: () => request<FilterOptions>('/demands/options'),
};

// ---- News API ----

export interface NewsListParams {
  category?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export const newsApi = {
  list: (params: NewsListParams) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.append(key, String(value));
      }
    });
    return request<PaginatedData<NewsArticle>>(
      `/news?${searchParams.toString()}`,
    );
  },

  detail: (id: number) => request<NewsArticle>(`/news/${id}`),

  categories: () => request<string[]>('/news/categories'),
};

// ---- Energy System API ----

export const energySystemApi = {
  get: () => request<EnergySystemData>('/energy-system'),
};

// ---- Profile API ----

export const profileApi = {
  get: () => request<ProfileUser>('/profile'),

  update: (data: Partial<ProfileUser>) =>
    request<ProfileUser>('/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  mySupplies: () => request<PersonalSupply[]>('/profile/supplies'),

  myDemands: () => request<PersonalDemand[]>('/profile/demands'),

  favorites: () => request<Favorite[]>('/profile/favorites'),

  messages: () => request<Message[]>('/profile/messages'),

  contacts: () =>
    request<ContactRecord[]>('/profile/contacts'),

  addFavorite: (type: 'supply' | 'demand', itemId: number) =>
    request<void>('/profile/favorites', {
      method: 'POST',
      body: JSON.stringify({ type, itemId } as AddFavoriteParams),
    }),

  removeFavorite: (id: number) =>
    request<void>(`/profile/favorites/${id}`, { method: 'DELETE' }),

  markMessageRead: (id: number) =>
    request<void>(`/profile/messages/${id}/read`, { method: 'PUT' }),

  editSupply: (id: number, data: GreenCertCreateParams) =>
    request<SupplyItem>(`/profile/supplies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  editDemand: (id: number, data: GreenCertCreateParams) =>
    request<DemandItem>(`/profile/demands/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  setSupplyStatus: (id: number, status: 'active' | 'offline') =>
    request<void>(`/profile/supplies/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),

  setDemandStatus: (id: number, status: 'active' | 'offline') =>
    request<void>(`/profile/demands/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),

  deleteSupply: (id: number) =>
    request<void>(`/profile/supplies/${id}`, { method: 'DELETE' }),

  deleteDemand: (id: number) =>
    request<void>(`/profile/demands/${id}`, { method: 'DELETE' }),
};

// ---- Ratings & Rankings API ----

export interface RankItem {
  id: number;
  type?: 'supply' | 'demand';
  company: string;
  projectType?: string;
  region: string;
  count?: number;
  ratingAvg?: number;
  ratingCount?: number;
}

export interface Rankings {
  hotProjects: RankItem[];
  hotUsers: RankItem[];
  activeBuyers: RankItem[];
  topRated: RankItem[];
}

export interface RatingView {
  id: number;
  raterId: number;
  raterName: string;
  score: number;
  comment: string;
  createdAt: string;
}

export interface PublicProfile {
  id: number;
  companyName: string;
  region: string;
  role: string;
  ratingAvg: number;
  ratingCount: number;
  supplies: PersonalSupply[];
  demands: PersonalDemand[];
}

export const rankingApi = {
  get: () => request<Rankings>('/rankings'),
};

export const ratingApi = {
  submit: (
    userId: number,
    body: { score: number; comment?: string; listingType: string; listingId: number },
  ) =>
    request<{ ok: boolean }>(`/users/${userId}/ratings`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  list: (userId: number, page = 1) =>
    request<{ list: RatingView[] }>(`/users/${userId}/ratings?page=${page}`),
};

export const userApi = {
  publicProfile: (userId: number) =>
    request<PublicProfile>(`/users/${userId}/profile-public`),
};

// ---- Admin API ----

export interface AdminInfo {
  id: number;
  username: string;
  name: string;
}

export interface AdminAuthResult {
  admin: AdminInfo;
  token: string;
}

export interface AdminStats {
  totalUsers: number;
  newUsersToday: number;
  totalSupply: number;
  pendingSupply: number;
  totalDemand: number;
  pendingDemand: number;
  totalReports: number;
  pendingReports: number;
  totalNews: number;
}

export interface AdminActivity {
  id: number;
  type: string;
  action: string;
  target: string;
  time: string;
}

export interface AdminChartData {
  weeklyUsers: { day: string; count: number }[];
  monthlyListings: { month: string; supply: number; demand: number }[];
}

export interface AdminDashboard {
  stats: AdminStats;
  recentActivities: AdminActivity[];
  chartData: AdminChartData;
}

export interface DailyActive {
  date: string;
  dau: number;
  pv: number;
}

export interface PagePv {
  path: string;
  pv: number;
}

export interface RegionPv {
  region: string;
  pv: number;
}

export interface AnalyticsData {
  todayPv: number;
  todayDau: number;
  totalPv: number;
  dailyTrend: DailyActive[];
  topPages: PagePv[];
  topRegions: RegionPv[];
}

export interface AdminUserItem {
  id: number;
  accountType: string;
  role: 'supplier' | 'buyer';
  companyName: string;
  creditCode: string;
  contactName: string;
  phone: string;
  region: string;
  email: string;
  avatar: string;
  status: 'active' | 'disabled';
  createdAt: string;
  listingCount: number;
}

export interface AdminListingItem {
  id: number;
  userId: number;
  company: string;
  region: string;
  direction: string;
  year: string;
  projectType: string[];
  quantity: number;
  price: string;
  publishDate: string;
  status: string;
  auditStatus: 'pending' | 'approved' | 'rejected';
}

export interface AdminReportItem {
  id: number;
  reporterId: number;
  reporterName: string;
  targetType: 'supply' | 'demand';
  targetId: number;
  targetTitle: string;
  reason: string;
  detail: string;
  status: 'pending' | 'resolved' | 'rejected';
  note: string;
  createdAt: string;
}

export const adminApi = {
  login: (username: string, password: string) =>
    request<AdminAuthResult>('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  dashboard: () => request<AdminDashboard>('/admin/dashboard'),

  analytics: (days = 14) => request<AnalyticsData>(`/admin/analytics?days=${days}`),

  listUsers: (params: { search?: string; role?: string; status?: string; page?: number; pageSize?: number }) => {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && v !== '' && sp.append(k, String(v)));
    return request<PaginatedData<AdminUserItem>>(`/admin/users?${sp.toString()}`);
  },
  setUserStatus: (id: number, status: 'active' | 'disabled') =>
    request<Record<string, never>>(`/admin/users/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),

  listSupplies: (params: { status?: string; search?: string; page?: number; pageSize?: number }) => {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && v !== '' && sp.append(k, String(v)));
    return request<PaginatedData<AdminListingItem>>(`/admin/supplies?${sp.toString()}`);
  },
  auditSupply: (id: number, action: 'approve' | 'reject', reason?: string) =>
    request<Record<string, never>>(`/admin/supplies/${id}/audit`, { method: 'PUT', body: JSON.stringify({ action, reason }) }),

  listDemands: (params: { status?: string; search?: string; page?: number; pageSize?: number }) => {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && v !== '' && sp.append(k, String(v)));
    return request<PaginatedData<AdminListingItem>>(`/admin/demands?${sp.toString()}`);
  },
  auditDemand: (id: number, action: 'approve' | 'reject', reason?: string) =>
    request<Record<string, never>>(`/admin/demands/${id}/audit`, { method: 'PUT', body: JSON.stringify({ action, reason }) }),

  listReports: (params: { status?: string; page?: number; pageSize?: number }) => {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && v !== '' && sp.append(k, String(v)));
    return request<PaginatedData<AdminReportItem>>(`/admin/reports?${sp.toString()}`);
  },
  handleReport: (id: number, action: 'resolve' | 'reject', note?: string) =>
    request<Record<string, never>>(`/admin/reports/${id}/handle`, { method: 'PUT', body: JSON.stringify({ action, note }) }),

  listNews: (params: { category?: string; search?: string; page?: number; pageSize?: number }) => {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && v !== '' && sp.append(k, String(v)));
    return request<PaginatedData<NewsArticle>>(`/admin/news?${sp.toString()}`);
  },
  createNews: (data: Partial<NewsArticle>) =>
    request<NewsArticle>('/admin/news', { method: 'POST', body: JSON.stringify(data) }),
  updateNews: (id: number, data: Partial<NewsArticle>) =>
    request<Record<string, never>>(`/admin/news/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  setNewsStatus: (id: number, status: 'published' | 'draft' | 'offline') =>
    request<Record<string, never>>(`/admin/news/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  deleteNews: (id: number) =>
    request<Record<string, never>>(`/admin/news/${id}`, { method: 'DELETE' }),
};

// ---- Tracking ----

export const trackApi = {
  pageview: (sessionId: string, path: string) =>
    request<Record<string, never>>('/track/pageview', {
      method: 'POST',
      body: JSON.stringify({ sessionId, path }),
    }).catch(() => {}),
};

// ---- Re-export request for custom calls ----
export { request, ApiError };